import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { Category } from '../data/types'
import { categories } from '../data/categories'

export interface MasterGameStat {
  correct: number
  total: number
  time: number
  score: number
  maxStreak: number
}

export interface GameSessionState {
  isPlaying: boolean
  isTimerPaused: boolean // Timer paused during result display
  elapsedTime: number // seconds elapsed (count up instead of down)
  totalScore: number
  multiplier: number
  selectedCategories: Category[]
  playedCategories: Category[]
  wordsLearned: number
  correctAnswers: number
  totalQuestions: number
  startTime: Date | null
  // Master Mode
  isMasterMode: boolean
  masterSessionId: string | null
  masterGamesCompleted: string[]
  masterGameStats: Record<string, MasterGameStat>
  masterTotalWords: number
}

interface GameSessionContextType {
  session: GameSessionState
  startSession: (categories: Category[], masterMode?: boolean, totalWords?: number) => void
  endSession: () => void
  addScore: (points: number, correct: boolean) => void
  markCategoryPlayed: (category: Category) => void
  isCategoryDisabled: (category: Category) => boolean
  getMultiplier: (categoryCount: number) => number
  resetSession: () => void
  pauseTimer: () => void
  resumeTimer: () => void
  completeMasterGame: (gameId: string, stat: MasterGameStat) => void
}

const GameSessionContext = createContext<GameSessionContextType | undefined>(undefined)

const SESSION_KEY = 'hsk2-game-session'

export function GameSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<GameSessionState>(() => {
    const saved = sessionStorage.getItem(SESSION_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      // If page was refreshed during a game (isPlaying: true), reset the session
      // This prevents issues with stale timer and game state
      if (parsed.isPlaying) {
        sessionStorage.removeItem(SESSION_KEY)
        return {
          isPlaying: false,
          isTimerPaused: false,
          elapsedTime: 0,
          totalScore: 0,
          multiplier: 1,
          selectedCategories: [],
          playedCategories: [],
          wordsLearned: 0,
          correctAnswers: 0,
          totalQuestions: 0,
          startTime: null,
          isMasterMode: false,
          masterSessionId: null,
          masterGamesCompleted: [],
          masterGameStats: {},
          masterTotalWords: 0,
        }
      }
      return {
        ...parsed,
        startTime: parsed.startTime ? new Date(parsed.startTime) : null,
      }
    }
    return {
      isPlaying: false,
      isTimerPaused: false,
      elapsedTime: 0,
      totalScore: 0,
      multiplier: 1,
      selectedCategories: [],
      playedCategories: [],
      wordsLearned: 0,
      correctAnswers: 0,
      totalQuestions: 0,
      startTime: null,
      isMasterMode: false,
      masterSessionId: null,
      masterGamesCompleted: [],
      masterGameStats: {},
      masterTotalWords: 0,
    }
  })

  // Save session to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  }, [session])

  // Timer count-up - only runs when playing AND not paused
  useEffect(() => {
    if (!session.isPlaying || session.isTimerPaused) return

    const timer = setInterval(() => {
      setSession(prev => ({ ...prev, elapsedTime: prev.elapsedTime + 1 }))
    }, 1000)

    return () => clearInterval(timer)
  }, [session.isPlaying, session.isTimerPaused])

  const getMultiplier = useCallback((categoryCount: number): number => {
    if (categoryCount <= 1) return 1.0
    // 1 cat = 1.0x, 2+ scales linearly up to all = 2.0x
    const total = categories.length
    if (categoryCount >= total) return 2.0
    return Math.round((1.0 + 1.0 * (categoryCount - 1) / (total - 1)) * 10) / 10
  }, [])

  const startSession = useCallback((selectedCategories: Category[], masterMode = false, totalWords = 0) => {
    const multiplier = getMultiplier(selectedCategories.length)
    setSession({
      isPlaying: true,
      isTimerPaused: false,
      elapsedTime: 0,
      totalScore: 0,
      multiplier,
      selectedCategories,
      playedCategories: [],
      wordsLearned: 0,
      correctAnswers: 0,
      totalQuestions: 0,
      startTime: new Date(),
      isMasterMode: masterMode,
      masterSessionId: masterMode ? `master-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : null,
      masterGamesCompleted: [],
      masterGameStats: {},
      masterTotalWords: totalWords,
    })
  }, [getMultiplier])

  const endSession = useCallback(() => {
    setSession(prev => ({ ...prev, isPlaying: false }))
  }, [])

  const addScore = useCallback((points: number, correct: boolean) => {
    setSession(prev => {
      const multipliedPoints = Math.round(points * prev.multiplier)
      return {
        ...prev,
        totalScore: prev.totalScore + multipliedPoints,
        correctAnswers: correct ? prev.correctAnswers + 1 : prev.correctAnswers,
        totalQuestions: prev.totalQuestions + 1,
        wordsLearned: correct ? prev.wordsLearned + 1 : prev.wordsLearned,
      }
    })
  }, [])

  const markCategoryPlayed = useCallback((category: Category) => {
    setSession(prev => {
      if (prev.playedCategories.includes(category)) return prev
      return { ...prev, playedCategories: [...prev.playedCategories, category] }
    })
  }, [])

  const isCategoryDisabled = useCallback((category: Category): boolean => {
    return session.playedCategories.includes(category)
  }, [session.playedCategories])

  const pauseTimer = useCallback(() => {
    setSession(prev => (prev.isMasterMode ? prev : { ...prev, isTimerPaused: true }))
  }, [])

  const resumeTimer = useCallback(() => {
    setSession(prev => (prev.isMasterMode ? prev : { ...prev, isTimerPaused: false }))
  }, [])

  const completeMasterGame = useCallback((gameId: string, stat: MasterGameStat) => {
    setSession(prev => {
      if (prev.masterGamesCompleted.includes(gameId)) return prev
      return {
        ...prev,
        masterGamesCompleted: [...prev.masterGamesCompleted, gameId],
        masterGameStats: { ...prev.masterGameStats, [gameId]: stat },
      }
    })
  }, [])

  const resetSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY)
    setSession({
      isPlaying: false,
      isTimerPaused: false,
      elapsedTime: 0,
      totalScore: 0,
      multiplier: 1,
      selectedCategories: [],
      playedCategories: [],
      wordsLearned: 0,
      correctAnswers: 0,
      totalQuestions: 0,
      startTime: null,
      isMasterMode: false,
      masterSessionId: null,
      masterGamesCompleted: [],
      masterGameStats: {},
      masterTotalWords: 0,
    })
  }, [])

  return (
    <GameSessionContext.Provider value={{
      session,
      startSession,
      endSession,
      addScore,
      markCategoryPlayed,
      isCategoryDisabled,
      getMultiplier,
      resetSession,
      pauseTimer,
      resumeTimer,
      completeMasterGame,
    }}>
      {children}
    </GameSessionContext.Provider>
  )
}

export function useGameSession() {
  const context = useContext(GameSessionContext)
  if (!context) {
    throw new Error('useGameSession must be used within a GameSessionProvider')
  }
  return context
}

// Helper to format time
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
